import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard, Briefcase, ShieldAlert, User, LogOut, ChevronRight } from "lucide-react";
import { getUser, clearSession } from "@/lib/api";
import { toast } from "sonner";

/**
 * Header global del producto.
 * - Desktop: muestra logo + slot `right` (acciones específicas de la página).
 * - Móvil (< md): añade un botón hamburguesa que despliega desde el propio
 *   header con Dashboard, Consultoría, Panel admin, Mi cuenta y Salir.
 */
export default function BrandHeader({ right = null, minimal = false }) {
  const user = getUser();
  const home = user ? "/dashboard" : "/";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const hasConsulting = (user?.products || []).includes("consulting") || user?.is_admin;

  // Cierra al navegar (por si el usuario cambia de ruta)
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  function logout() {
    clearSession();
    toast.success("Sesión cerrada");
    navigate("/");
  }

  // Ítems del menú móvil (solo visible si hay sesión)
  const items = user
    ? [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, show: true, testid: "menu-dashboard" },
        { to: "/consulting", label: "Consultoría", icon: Briefcase, show: hasConsulting, gold: true, testid: "menu-consulting" },
        { to: "/admin", label: "Panel admin", icon: ShieldAlert, show: !!user.is_admin, testid: "menu-admin" },
        { to: "/cuenta", label: "Mi cuenta", icon: User, show: true, testid: "menu-account" },
      ].filter((it) => it.show)
    : [];

  return (
    <header
      className="w-full backdrop-blur-md bg-white/80 border-b border-gray-100 sticky top-0 z-30"
      data-testid="brand-header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-10 h-16 flex items-center justify-between gap-3 min-w-0">
        <Link
          to={home}
          className="flex items-center gap-2.5 min-w-0 shrink-0 group transition-transform hover:-translate-y-px"
          data-testid="brand-header-logo-link"
          aria-label="Ir al inicio"
        >
          <div
            className="w-9 h-9 rounded-xl overflow-hidden shadow-sm shrink-0"
            aria-hidden="true"
          >
            <img
              src="/apple-touch-icon.png"
              alt=""
              className="w-full h-full object-cover"
              width="36"
              height="36"
              loading="eager"
              decoding="async"
            />
          </div>
          <div className="leading-tight min-w-0">
            <div className="hidden sm:block font-display text-[14px] sm:text-[15px] font-semibold text-slate-900 tracking-tight truncate">
              Talent Health Check
            </div>
            {!minimal && (
              <div className="hidden sm:block text-[11px] text-slate-400 -mt-0.5">
                by OptimizaRRHH
              </div>
            )}
          </div>
        </Link>

        {/* Slot derecho: en desktop se muestra tal cual; en móvil se oculta y
            aparece la hamburguesa cuando hay sesión. */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="hidden md:flex items-center min-w-0">{right}</div>

          {/* Móvil: accesos directos por icono junto al hamburguesa */}
          {user && items.length > 0 && (
            <nav
              className="md:hidden flex items-center gap-1"
              aria-label="Accesos rápidos"
              data-testid="brand-header-mobile-quicknav"
            >
              {items.map((it) => {
                const Icon = it.icon;
                const active = location.pathname.startsWith(it.to);
                return (
                  <Link
                    key={`quick-${it.to}`}
                    to={it.to}
                    aria-label={it.label}
                    title={it.label}
                    className={`w-9 h-9 rounded-xl inline-flex items-center justify-center border transition ${
                      active
                        ? "border-[#0F6E56] bg-[#0F6E56]/10 text-[#0F6E56]"
                        : "border-slate-200 bg-white text-slate-700 hover:text-[#0F6E56] hover:border-[#0F6E56]"
                    }`}
                    data-testid={`${it.testid}-quick`}
                  >
                    <Icon
                      className="w-[18px] h-[18px]"
                      strokeWidth={2}
                      style={it.gold && !active ? { color: "#C9A84C" } : {}}
                    />
                  </Link>
                );
              })}
              <button
                onClick={logout}
                aria-label="Salir"
                title="Salir"
                className="w-9 h-9 rounded-xl inline-flex items-center justify-center border border-slate-200 bg-white text-red-600 hover:bg-red-50 hover:border-red-300 transition"
                data-testid="menu-logout-quick"
              >
                <LogOut className="w-[18px] h-[18px]" strokeWidth={2} />
              </button>
            </nav>
          )}

          {user && (
            <div className="md:hidden relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="w-10 h-10 rounded-xl inline-flex items-center justify-center border border-slate-200 bg-white text-slate-700 hover:text-[#0F6E56] hover:border-[#0F6E56] transition"
                aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
                aria-expanded={menuOpen}
                data-testid="brand-header-menu-toggle"
              >
                {menuOpen ? <X className="w-5 h-5" strokeWidth={2} /> : <Menu className="w-5 h-5" strokeWidth={2} />}
              </button>

              {menuOpen && (
                <>
                  <span
                    aria-hidden
                    className="fixed inset-0 bg-black/20 backdrop-blur-[2px] md:hidden -z-0"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div
                    className="absolute right-0 top-full mt-2 w-64 rounded-2xl overflow-hidden z-50"
                    style={{
                      background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
                      boxShadow: "0 24px 60px -20px rgba(11,61,46,0.35), 0 0 0 1px rgba(11,61,46,0.08)",
                    }}
                    role="menu"
                    data-testid="brand-header-menu"
                  >
                    <div className="px-4 py-3 border-b border-slate-100">
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Sesión</div>
                      <div className="text-sm font-semibold text-slate-800 truncate mt-0.5" title={user.email}>{user.email}</div>
                    </div>
                    <div className="py-2">
                      {items.map((it) => {
                        const Icon = it.icon;
                        const active = location.pathname.startsWith(it.to);
                        return (
                          <Link
                            key={it.to}
                            to={it.to}
                            role="menuitem"
                            className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                              active ? "bg-[#0F6E56]/8 text-[#0F6E56]" : "text-slate-700 hover:bg-slate-50"
                            }`}
                            data-testid={it.testid}
                          >
                            <Icon
                              className="w-4 h-4"
                              strokeWidth={2}
                              style={it.gold ? { color: "#C9A84C" } : {}}
                            />
                            <span className="flex-1">{it.label}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                          </Link>
                        );
                      })}
                    </div>
                    <div className="border-t border-slate-100 py-2">
                      <button
                        onClick={logout}
                        role="menuitem"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                        data-testid="menu-logout"
                      >
                        <LogOut className="w-4 h-4" strokeWidth={2} />
                        Salir
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
